import React, { useState } from 'react';
import { Save } from 'lucide-react';
import Button from '../Button';
import { shipmentsApi } from '../../utils/shipmentsApi';
import { SETTING_FIELDS, changedSettings, rangeText, toInputValues, validateSettings } from './settingsFields';
import { TextField } from './FormFields';
import { Banner } from './Ui';

// Settings form (PUT /settings). The inputs are validated against the contract ranges before anything is sent;
// the server answer replaces the data. The parent remounts the form (key) when new settings arrive.
// `aiLimitFromEnv`: number when the server ENV (SHIPMENTS_AI_DAILY_LIMIT) overrides the daily AI limit, else null.
const SettingsForm = ({ settings, aiLimitFromEnv = null, onSaved, notify }) => {
  const overridden = Number.isFinite(aiLimitFromEnv) && aiLimitFromEnv > 0;
  const locked = overridden ? ['aiDailyLimit'] : [];
  const [values, setValues] = useState(() => toInputValues(settings));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState('');

  const changed = changedSettings(values, settings, locked);
  const dirty = Object.keys(changed).length > 0;

  const update = (key) => (e) => {
    const next = { ...values, [key]: e.target.value };
    setValues(next);
    if (errors[key]) setErrors((prev) => { const { [key]: _removed, ...rest } = prev; return rest; });
  };
  const checkOne = (key) => () => {
    const found = validateSettings(values, locked)[key];
    if (found) setErrors((prev) => ({ ...prev, [key]: found }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const found = validateSettings(values, locked);
    setErrors(found);
    setServerError('');
    if (Object.keys(found).length > 0) return;
    setSaving(true);
    try {
      const payload = await shipmentsApi.saveSettings(changedSettings(values, settings, locked));
      notify.success('Nastavení bylo uloženo.');
      onSaved(payload);
    } catch (err) {
      setServerError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate>
      {serverError && <Banner tone="bad">{serverError}</Banner>}
      <div className="shp-settings-grid">
        {SETTING_FIELDS.map((f) => {
          if (f.key === 'aiDailyLimit' && overridden) {
            // the ENV wins over the stored value: shown as read-only with the reason
            return (
              <TextField
                key={f.key} label={`${f.label} (${f.unit})`} type="text" value={String(aiLimitFromEnv)} readOnly aria-readonly="true"
                hint={`Hodnotu určuje konfigurace serveru (SHIPMENTS_AI_DAILY_LIMIT = ${aiLimitFromEnv}). Uložená hodnota ${settings?.aiDailyLimit ?? '–'} se nepoužije.`}
              />
            );
          }
          const extra = f.key === 'aiDailyLimit' ? ' Změna se projeví zhruba do minuty.' : '';
          return (
            <TextField
              key={f.key} label={`${f.label} (${f.unit})`} type="text" inputMode={f.float ? 'decimal' : 'numeric'} autoComplete="off"
              value={values[f.key]} error={errors[f.key]} onChange={update(f.key)} onBlur={checkOne(f.key)}
              hint={`${f.help}${extra} Rozsah: ${rangeText(f)}.`}
            />
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginTop: 18 }}>
        <Button type="submit" icon={Save} loading={saving} disabled={!dirty}>Uložit nastavení</Button>
        {!dirty && <span className="shp-small shp-muted">Žádné neuložené změny.</span>}
      </div>
    </form>
  );
};

export default SettingsForm;
